/**
 * Dashboard Utility Functions
 * Pure functions for calculations and data transformations
 */

import { Timestamp } from 'firebase/firestore';
import type { Milestone, ActionItem, Issue, Rock, User } from '../types';
import type { CollaboratorWithTypes, RockWithDetails } from './dashboard.types';

/**
 * Calculate current quarter (Q1-Q4)
 * @returns Quarter string (e.g., "Q1", "Q2", "Q3", "Q4")
 */
export function calculateQuarter(): string {
  const now = new Date();
  const month = now.getMonth(); // 0-11
  const quarter = Math.floor(month / 3) + 1; // 1-4
  return `Q${quarter}`;
}

/**
 * Calculate current week within the quarter (1-13)
 * @returns Object with current week and total weeks (13)
 */
export function calculateWeek(): { current: number; total: number } {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const diffTime = now.getTime() - startOfYear.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const weekNumber = Math.ceil((diffDays + startOfYear.getDay() + 1) / 7);
  const weekInQuarter = ((weekNumber - 1) % 13) + 1;
  return { current: weekInQuarter, total: 13 };
}

/**
 * Calculate progress percentage from milestones
 * @param milestones Array of milestones
 * @returns Progress percentage (0-100)
 */
export function calculateMilestoneProgress(milestones: Milestone[]): number {
  if (!milestones || milestones.length === 0) return 0;
  const completed = milestones.filter((m) => m.complete).length;
  return Math.round((completed / milestones.length) * 100);
}

/**
 * Get collaborators with type indicators (R = Rock collaborator, T = Task assignee)
 * @param rock Rock with details
 * @param users Array of all users
 * @param actionItems Array of action items for the rock
 * @returns Array of collaborators with their types
 */
export function getCollaboratorsWithTypes(
  rock: RockWithDetails,
  users: User[],
  actionItems: ActionItem[]
): CollaboratorWithTypes[] {
  const collaboratorMap: Record<string, CollaboratorWithTypes> = {};

  // Add rock collaborators (type 'R')
  if (rock.collaboratorUsers && rock.collaboratorUsers.length > 0) {
    rock.collaboratorUsers.forEach((user) => {
      if (user && user.name) {
        collaboratorMap[user.id] = { name: user.name, types: ['R'] };
      }
    });
  }

  // Add action item assignees (type 'T')
  if (actionItems && actionItems.length > 0) {
    const incompleteItems = actionItems.filter((item) => !item.complete);

    incompleteItems.forEach((item) => {
      if (item.assigneeId) {
        const assignee = users.find((u) => u.id === item.assigneeId);
        if (assignee) {
          if (collaboratorMap[assignee.id]) {
            if (!collaboratorMap[assignee.id].types.includes('T')) {
              collaboratorMap[assignee.id].types.push('T');
            }
          } else {
            collaboratorMap[assignee.id] = { name: assignee.name, types: ['T'] };
          }
        }
      }
    });
  }

  return Object.values(collaboratorMap);
}

/**
 * Format collaborator types as string (e.g., "R,T" or "R" or "T")
 * @param types Array of collaborator types
 * @returns Comma-separated string of types
 */
export function formatCollaboratorTypes(types: ('R' | 'T')[]): string {
  return types.join(',');
}

/**
 * Get unsolved issues for a specific rock
 * @param rockId Rock ID
 * @param issues Array of all issues
 * @returns Array of unsolved issues for the rock
 */
export function getRockIssues(rockId: string, issues: Issue[]): Issue[] {
  return issues.filter(
    (issue) => issue.relatedRockId === rockId && issue.status !== 'solved'
  );
}

/**
 * Format date without timezone conversion issues
 * @param date Date, Timestamp, or string
 * @param options Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string
 */
export function formatDateWithoutTimezone(
  date: Date | Timestamp | string,
  options?: Intl.DateTimeFormatOptions
): string {
  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else if (date && typeof date === 'object' && 'toDate' in date) {
    // Firestore Timestamp
    dateObj = (date as Timestamp).toDate();
  } else if (typeof date === 'string') {
    // Handle ISO string - extract date part to avoid timezone issues
    const datePart = date.split('T')[0];
    const [year, month, day] = datePart.split('-');
    dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  } else {
    // Fallback: try to convert to Date
    dateObj = new Date(date as any);
  }

  if (options) {
    return dateObj.toLocaleDateString('en-US', options);
  }
  return dateObj.toLocaleDateString('en-US');
}

